// =========================================
// useFeedback — Active learning feedback on criterion scores
// =========================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

// =========================================
// Types
// =========================================

export interface LearningFeedback {
  id: string;
  user_id: string;
  evaluation_id: string;
  criteria_id: string;
  original_score: number | null;
  corrected_score: number | null;
  feedback_note: string | null;
  created_at: string;
}

export interface SubmitFeedbackInput {
  evaluation_id: string;
  criteria_id: string;
  original_score: number;
  corrected_score: number;
  feedback_note?: string;
}

// =========================================
// Hook
// =========================================

export function useFeedback(evaluationId: string) {
  const queryClient = useQueryClient();
  const queryKey = ['feedback', evaluationId];

  // Fetch all feedback for a given evaluation
  const feedbackQuery = useQuery({
    queryKey,
    queryFn: async (): Promise<LearningFeedback[]> => {
      const { data, error } = await supabase
        .from('learning_feedback')
        .select('*')
        .eq('evaluation_id', evaluationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as LearningFeedback[];
    },
    enabled: !!evaluationId,
  });

  // Get feedback for a specific criterion
  function getFeedbackForCriterion(criteriaId: string): LearningFeedback | undefined {
    return feedbackQuery.data?.find((f) => f.criteria_id === criteriaId);
  }

  // Submit or update feedback
  const submitFeedback = useMutation({
    mutationFn: async (input: SubmitFeedbackInput) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check for existing feedback on this eval+criterion
      const existing = feedbackQuery.data?.find(
        (f) => f.criteria_id === input.criteria_id
      );

      if (existing) {
        const { error } = await supabase
          .from('learning_feedback')
          .update({
            corrected_score: input.corrected_score,
            feedback_note: input.feedback_note ?? null,
          })
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('learning_feedback')
          .insert({
            user_id: user.id,
            evaluation_id: input.evaluation_id,
            criteria_id: input.criteria_id,
            original_score: input.original_score,
            corrected_score: input.corrected_score,
            feedback_note: input.feedback_note ?? null,
          });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Feedback saved');
    },
    onError: (err: Error) => {
      toast.error(`Failed to save feedback: ${err.message}`);
    },
  });

  // Delete feedback
  const deleteFeedback = useMutation({
    mutationFn: async (feedbackId: string) => {
      const { error } = await supabase
        .from('learning_feedback')
        .delete()
        .eq('id', feedbackId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Feedback removed');
    },
    onError: (err: Error) => {
      toast.error(`Failed to remove feedback: ${err.message}`);
    },
  });

  return {
    feedback: feedbackQuery.data ?? [],
    isLoading: feedbackQuery.isLoading,
    getFeedbackForCriterion,
    submitFeedback,
    deleteFeedback,
  };
}
